import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { usePlay, Panel } from "./usePlay";
import { fmtAZN } from "@/lib/api";
import { sfx } from "@/lib/sound";

const ORDER = [0, 32, 15, 19, 4, 21, 2, 25, 17, 34, 6, 27, 13, 36, 11, 30, 8, 23, 10, 5, 24, 16, 33, 1, 20, 14, 31, 9, 22, 18, 29, 7, 28, 12, 35, 3, 26];
const REDS = new Set([1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36]);
const CHIPS = [0.2, 1, 5, 10, 50];
const colorOf = (n) => (n === 0 ? "#15803d" : REDS.has(n) ? "#b91c1c" : "#18181b");
const A = 360 / 37, R = 190;

const Wheel = ({ rot, onDone }) => (
  <div className="relative w-full max-w-[400px] aspect-square mx-auto">
    <div className="absolute left-1/2 top-2 -translate-x-1/2 z-10 w-5 h-5 rounded-full bg-white shadow-[0_0_12px_#fff]" data-testid="roulette-ball" />
    <motion.svg viewBox="-200 -200 400 400" className="w-full h-full" animate={{ rotate: rot }} transition={{ duration: 5, ease: [0.12, 0.65, 0.2, 1] }} onAnimationComplete={onDone}>
      <circle r="198" fill="#5b3a1a" stroke="#D4AF37" strokeWidth="4" />
      {ORDER.map((n, i) => {
        const a0 = ((i * A - 90 - A / 2) * Math.PI) / 180, a1 = ((i * A - 90 + A / 2) * Math.PI) / 180;
        return (
          <g key={n}>
            <path d={`M0,0 L${R * Math.cos(a0)},${R * Math.sin(a0)} A${R},${R} 0 0 1 ${R * Math.cos(a1)},${R * Math.sin(a1)} Z`} fill={colorOf(n)} stroke="#D4AF37" strokeWidth="0.8" />
            <text transform={`rotate(${i * A}) translate(0,-170)`} fill="#fff" fontSize="12" fontWeight="700" textAnchor="middle" dominantBaseline="middle">{n}</text>
          </g>
        );
      })}
      <circle r="120" fill="#3b2410" stroke="#D4AF37" strokeWidth="3" />
      <circle r="60" fill="#2a1a0b" stroke="#b8921f" strokeWidth="3" />
      {[0, 45, 90, 135].map((d) => <rect key={d} x="-4" y="-58" width="8" height="116" rx="4" fill="#D4AF37" transform={`rotate(${d})`} />)}
      <circle r="14" fill="#FFD700" />
    </motion.svg>
  </div>
);

const Cell = ({ k, label, bg, bets, onBet, className = "", testid }) => (
  <button onClick={() => onBet(k)} className={`relative border border-white/20 font-bold text-xs sm:text-sm hover:brightness-125 transition ${className}`} style={{ background: bg }} data-testid={testid}>
    {label}
    {bets[k] && <span className="absolute -top-1.5 -right-1.5 z-10 min-w-[24px] h-6 px-1 rounded-full bg-[#FFD700] text-black text-[10px] font-black flex items-center justify-center border-2 border-dashed border-white">{bets[k].amount}</span>}
  </button>
);

export const RouletteGame = ({ mode }) => {
  const { busy, play, finish } = usePlay(mode);
  const [chip, setChip] = useState(1);
  const [bets, setBets] = useState({});
  const [rot, setRot] = useState(0);
  const [res, setRes] = useState(null);
  const [hist, setHist] = useState([]);
  const pending = useRef(null);
  const tick = useRef(null);
  const total = Object.values(bets).reduce((s, b) => s + b.amount, 0);

  const add = (k) => {
    if (busy) return;
    const [type, v] = k.split(":");
    sfx.click();
    setBets((b) => ({ ...b, [k]: { type, value: Number(v || 0), amount: Math.round(((b[k]?.amount || 0) + chip) * 100) / 100 } }));
  };

  const spin = async () => {
    if (!total) return toast.error("Əvvəlcə masaya fiş qoyun");
    setRes(null);
    const d = await play("/games/roulette", { bets: Object.values(bets) });
    if (!d) return;
    pending.current = d;
    const idx = ORDER.indexOf(d.number);
    setRot((r) => r - (r % 360) + 360 * 5 + (360 - idx * A));
    tick.current = setInterval(sfx.reelTick, 110);
  };

  const done = () => {
    const d = pending.current;
    if (!d) return;
    pending.current = null;
    clearInterval(tick.current);
    sfx.reelStop();
    setRes(d); setHist((h) => [d.number, ...h].slice(0, 12)); finish(d);
  };

  const P = { bets, onBet: add };
  return (
    <div className="space-y-6" data-testid="roulette-game">
      <div className="grid lg:grid-cols-[1fr_1.4fr] gap-6 items-center">
        <div className="machine-frame rounded-3xl p-5">
          <Wheel rot={rot} onDone={done} />
          <div className="flex items-center justify-center gap-3 mt-4 h-12">
            {res && <span className="w-12 h-12 rounded-full flex items-center justify-center font-display font-black text-xl border-2 border-[#FFD700]" style={{ background: colorOf(res.number) }} data-testid="roulette-result">{res.number}</span>}
            {res && <span className={`font-display font-bold ${res.win ? "text-emerald-400" : "text-red-400"}`}>{res.win ? `+${fmtAZN(res.win)}` : "Uduzdunuz"}</span>}
          </div>
        </div>
        <div className="space-y-4 min-w-0">
          <div className="flex gap-1.5 overflow-x-auto min-h-[32px]" data-testid="roulette-history">
            {hist.map((n, i) => <span key={i} className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: colorOf(n) }}>{n}</span>)}
          </div>
          <div className="overflow-x-auto pb-2">
            <div className="min-w-[640px] rounded-2xl p-3 bg-[#0f5132] border-4 border-[#5b3a1a] shadow-inner" data-testid="roulette-board">
              <div className="grid grid-cols-[50px_repeat(12,1fr)_50px] grid-rows-3 h-[150px]">
                <Cell k="number:0" label="0" bg="#15803d" className="row-span-3 rounded-l-xl" testid="rbet-number-0" {...P} />
                {[3, 2, 1].map((row) => [
                  ...Array.from({ length: 12 }, (_, c) => {
                    const n = c * 3 + row;
                    return <Cell key={n} k={`number:${n}`} label={n} bg={colorOf(n)} testid={`rbet-number-${n}`} {...P} />;
                  }),
                  <Cell key={`col${row}`} k={`column:${row - 1}`} label="2:1" bg="transparent" testid={`rbet-column-${row - 1}`} {...P} />,
                ])}
              </div>
              <div className="grid grid-cols-[50px_repeat(3,1fr)_50px] h-10">
                <div />
                {[0, 1, 2].map((d) => <Cell key={d} k={`dozen:${d}`} label={`${d * 12 + 1}-${d * 12 + 12}`} bg="transparent" testid={`rbet-dozen-${d}`} {...P} />)}
              </div>
              <div className="grid grid-cols-[50px_repeat(6,1fr)_50px] h-10">
                <div />
                <Cell k="low" label="1-18" bg="transparent" testid="rbet-low" {...P} />
                <Cell k="even" label="CÜT" bg="transparent" testid="rbet-even" {...P} />
                <Cell k="red" label="QIRMIZI" bg="#b91c1c" testid="rbet-red" {...P} />
                <Cell k="black" label="QARA" bg="#18181b" testid="rbet-black" {...P} />
                <Cell k="odd" label="TƏK" bg="transparent" testid="rbet-odd" {...P} />
                <Cell k="high" label="19-36" bg="transparent" testid="rbet-high" {...P} />
              </div>
            </div>
          </div>
          <Panel>
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-xs uppercase tracking-[0.2em] text-zinc-500">Fiş</span>
              {CHIPS.map((c) => (
                <button key={c} onClick={() => setChip(c)} className={`w-11 h-11 rounded-full text-xs font-black border-4 border-dashed transition-transform ${chip === c ? "scale-110 border-white bg-[#D4AF37] text-black" : "border-white/40 bg-[#c4112f]"}`} data-testid={`chip-${c}`}>{c}</button>
              ))}
              <span className="ml-auto text-sm">Cəmi: <b className="text-[#FFD700]" data-testid="roulette-total">{fmtAZN(total)}</b></span>
            </div>
            <div className="flex gap-3">
              <button disabled={busy} onClick={() => setBets({})} className="btn-ghost rounded-full px-6 py-3" data-testid="roulette-clear-btn">Təmizlə</button>
              <button disabled={busy} onClick={spin} className="btn-gold flex-1 rounded-full py-3 font-display font-bold" data-testid="roulette-spin-btn">Fırlat</button>
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
};
